import type { OwnerType, SortOption } from '@coffee-to-go/common';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface FilterState {
  ownerTypes: OwnerType[];
  categories: string[];
  verticals: string[];
  stages: string[];
  activity: string[];
  minimumRating: number; // Minimum star rating (0-5)
  priorities: number[]; // Priority levels (empty = All, [2000] = Top only, [1000, 2000] = High and Top, etc.)
  boost: boolean | null; // Boost filter (null = All, true = Boosted only, false = Non-boosted only)
  tgs: string[];
  pds: string[];
  competitors: string[];
}

interface FilterStore {
  filters: FilterState;
  sortBy: SortOption;
  searchTerm: string;
  actions: {
    setFilters: (filters: FilterState) => void;
    setSortBy: (sortBy: SortOption) => void;
    setSearchTerm: (searchTerm: string) => void;
  };
}

const defaultFilters: FilterState = {
  ownerTypes: [],
  categories: [],
  verticals: [],
  stages: [],
  activity: ['live'],
  minimumRating: 0,
  priorities: [],
  boost: null,
  tgs: [],
  pds: [],
  competitors: [],
};

const useFilterStore = create<FilterStore>()(
  persist(
    (set) => ({
      filters: defaultFilters,
      sortBy: 'distance',
      searchTerm: '',
      actions: {
        setFilters: (filters) => set({ filters }),
        setSortBy: (sortBy) => set({ sortBy }),
        setSearchTerm: (searchTerm) => set({ searchTerm }),
      },
    }),
    {
      name: 'filter-store',
      partialize: (state) => ({
        filters: state.filters,
        sortBy: state.sortBy,
      }),
      version: 6,
      migrate: (persistedState: unknown) => {
        const state = persistedState as { filters: FilterState; sortBy: SortOption };
        return {
          filters: {
            ...defaultFilters,
            ...state.filters,
          },
          sortBy: state.sortBy || 'distance',
        };
      },
    },
  ),
);

export const useFilterStoreFilters = () => useFilterStore((state) => state.filters);
export const useFilterStoreSortBy = () => useFilterStore((state) => state.sortBy);
export const useFilterStoreSearchTerm = () => useFilterStore((state) => state.searchTerm);

export const useFilterStoreActions = () => useFilterStore((state) => state.actions);
